import { api } from '@/api/endpoints';
import { StorageKeys } from '@/lib/config';
import { createLogger } from '@/lib/logger';

const log = createLogger('push');

const REREGISTER_INTERVAL_MS = 24 * 60 * 60 * 1000;

export type PushState = 'unsupported' | 'denied' | 'subscribed' | 'off';

/**
 * Web Push helper for the fire-and-forget "new work in your queue" nudge.
 *
 * iOS requires the app to be INSTALLED to the home screen before push works,
 * and the permission request must come from a user tap. Android Chrome works
 * in-browser or installed. All calls are best-effort: push failing must never
 * affect the workflow.
 */
export const pushService = {
  isSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  },

  /** Decodes the base64url VAPID key into the format subscribe() expects. */
  urlBase64ToUint8Array(base64Url: string): Uint8Array<ArrayBuffer> {
    const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
    const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    // Explicit ArrayBuffer (not ArrayBufferLike) so the result satisfies the
    // BufferSource type pushManager.subscribe() requires.
    const output = new Uint8Array(new ArrayBuffer(raw.length));
    for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
    return output;
  },

  async getState(): Promise<PushState> {
    if (!this.isSupported()) return 'unsupported';
    if (Notification.permission === 'denied') return 'denied';
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      return sub ? 'subscribed' : 'off';
    } catch {
      return 'off';
    }
  },

  /**
   * Enable notifications. MUST be called from a user gesture (iOS refuses the
   * permission prompt otherwise). Returns the resulting state.
   */
  async enable(): Promise<PushState> {
    if (!this.isSupported()) return 'unsupported';
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';

      const { publicKey } = await api.pushPublicKey();
      const reg = await navigator.serviceWorker.ready;
      const sub =
        (await reg.pushManager.getSubscription()) ??
        (await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(publicKey),
        }));

      const json = sub.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        log.warn('push subscription missing fields');
        return 'off';
      }
      await api.pushSubscribe({
        endpoint: json.endpoint,
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
      });
      return 'subscribed';
    } catch (err) {
      log.warn('push enable failed', err);
      return 'off';
    }
  },

  /**
   * Re-assert this device's push subscription with the server, at most once
   * per day. The SERVER side of a subscription can be lost while the device
   * still believes it is subscribed (App_Data loss on the server, endpoint
   * eviction) and the driver then silently stops receiving pushes. The store
   * upserts by endpoint, so this is idempotent. The stamp is only written on
   * success; failures retry on the next sync cycle.
   */
  async ensureServerRegistration(): Promise<void> {
    try {
      const last = localStorage.getItem(StorageKeys.pushReregisterAt);
      if (last && Date.now() - Date.parse(last) < REREGISTER_INTERVAL_MS) return;
      if ((await this.getState()) !== 'subscribed') return;
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (!sub) return;
      const json = sub.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return;
      await api.pushSubscribe({
        endpoint: json.endpoint,
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
      });
      localStorage.setItem(StorageKeys.pushReregisterAt, new Date().toISOString());
    } catch (err) {
      log.debug('push re-register failed', err);
    }
  },

  /** Disable on this device. Best-effort on both sides. */
  async disable(): Promise<void> {
    if (!this.isSupported()) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (!sub) return;
      const endpoint = sub.endpoint;
      await sub.unsubscribe().catch(() => undefined);
      await api.pushUnsubscribe(endpoint).catch(() => undefined);
    } catch (err) {
      log.debug('push disable failed', err);
    }
  },
};
